/**
 * The receipt grid, as decisions rather than as markup.
 *
 * Everything here is a pure function of the rows the API sent, for the same
 * reason `src/lib/split-draft.ts` exists: the interesting half of a grid of
 * pictures is which picture it asks the network for and in what order the
 * tiles come out, and neither of those needs a DOM to be wrong in.
 *
 * **The one rule the whole file exists to hold: a tile never names the
 * full-size file.** `thumbnailUrl` is `?variant=thumb`, which is a 400px WebP
 * of tens of kilobytes; `url` is the 2000px original, which is hundreds. A
 * screen of forty tiles that reached for `url` — as a fallback for a missing
 * thumbnail, say, or because an `<img>` "needs a src" — would pull tens of
 * megabytes through the Worker to draw forty things the size of a stamp, on
 * the connection of somebody standing in a car park. So `tileFor` cannot
 * return the full-size URL: a receipt with no thumbnail is an *icon*, which is
 * a fact about the file rather than a failure, and `tests/receipt-library.test.ts`
 * asserts that over every shape of row there is.
 *
 * The full-size file is fetched in exactly one place — the viewer, one receipt
 * at a time, because that is when somebody has asked to read the total.
 */

import type { LibraryReceipt, Receipt } from '../api'
import { formatMonth, formatMoney, monthKeyOf } from './format'

/** What a tile draws. Note that neither case can carry `receipt.url`. */
export type Tile =
  | { kind: 'thumbnail'; src: string; alt: string }
  | { kind: 'icon'; glyph: string; label: string; alt: string }

/**
 * The thumbnail, or the file's own icon — never the full-size image.
 *
 * A missing thumbnail is ordinary: a PDF the browser would not render, or an
 * image encoded on a browser without `OffscreenCanvas`. It is not a broken
 * tile and it is not a reason to download the original.
 */
export function tileFor(receipt: Receipt): Tile {
  const name = nameOf(receipt)

  if (receipt.thumbnailUrl !== null) {
    return { kind: 'thumbnail', src: receipt.thumbnailUrl, alt: name }
  }
  return {
    kind: 'icon',
    glyph: receipt.contentType === 'application/pdf' ? 'PDF' : '🧾',
    label: receipt.contentType === 'application/pdf' ? 'PDF' : 'Receipt',
    alt: `${name} — no preview`,
  }
}

/** What to call one: what the person called it, or what it is. */
export function nameOf(receipt: Receipt): string {
  const named = receipt.originalName?.trim()
  if (named) return named
  return receipt.contentType === 'application/pdf' ? 'Receipt.pdf' : 'Receipt'
}

// ---------------------------------------------------------------------------
// The order the grid comes out in
// ---------------------------------------------------------------------------

export type Section = {
  key: string
  heading: string
  /** The one section that is a to-do list rather than a date. */
  unfiled: boolean
  rows: LibraryReceipt[]
}

/**
 * The page, cut into headings — the unfiled ones first, then by month.
 *
 * It groups rather than sorts. The server already returned the rows in
 * `(unfiled first, newest first)` order, held that way by `receipts_library`,
 * and re-sorting here would be a second opinion about an order that is the
 * database's to hold — and one that would go wrong the moment a second page
 * arrived, because a client can only sort the rows it happens to have.
 *
 * The unfiled section is dated by nothing, deliberately. Those receipts are a
 * pile of paper somebody is going to work through, and cutting four of them
 * into three months is filing them in the one way that does not help.
 */
export function sectionsFor(receipts: readonly LibraryReceipt[]): Section[] {
  const sections: Section[] = []

  for (const receipt of receipts) {
    if (isUnfiled(receipt)) {
      const first = sections[0]
      if (first?.unfiled) first.rows.push(receipt)
      else {
        sections.unshift({ key: 'unfiled', heading: 'Not filed yet', unfiled: true, rows: [receipt] })
      }
      continue
    }

    const monthKey = monthKeyOf(dayOf(receipt))
    const last = sections[sections.length - 1]
    if (last && !last.unfiled && last.key === monthKey) last.rows.push(receipt)
    else {
      sections.push({
        key: monthKey,
        heading: formatMonth(monthKey),
        unfiled: false,
        rows: [receipt],
      })
    }
  }

  return sections
}

/**
 * Whether this one still has something to do about it.
 *
 * A receipt filed on an expense that has since been *removed* counts as
 * unfiled, and that is the useful reading rather than a pedantic one: the
 * expense it explained is not on the group's screen any more, so the receipt
 * is loose in every sense a person cares about, and calling it filed would
 * hide it behind a link to nothing. The server does not rewrite the link —
 * what happened, happened — it just says `deletedAt`, and this is where that
 * is read.
 */
export function isUnfiled(receipt: LibraryReceipt): boolean {
  return receipt.transaction === null || receipt.transaction.deletedAt !== null
}

/** The day a receipt belongs to: its expense's, or the day it was taken. */
export function dayOf(receipt: LibraryReceipt): string {
  if (receipt.transaction !== null && receipt.transaction.deletedAt === null) {
    return receipt.transaction.occurredOn
  }
  return receipt.createdAt.slice(0, 10)
}

// ---------------------------------------------------------------------------
// Making an expense out of one
// ---------------------------------------------------------------------------

/**
 * The date the expense sheet opens on when it is opened from a receipt.
 *
 * The day the *photograph* was taken, not today. That is the whole point of
 * filing in the evening: the receipt is from lunchtime, or from three days ago
 * when the phone was last online, and an expense sheet that helpfully defaults
 * to today puts every one of those on the wrong day — quietly, in a field
 * nobody re-reads, on a row that is then sorted into the wrong month.
 *
 * `createdAt` is `datetime('now')`'s format in UTC: `YYYY-MM-DD HH:MM:SS`. The
 * date part is taken as a string rather than through `new Date`, because
 * parsing it and formatting it back is two chances to move the day across a
 * timezone and no chance at all of it becoming more correct.
 */
export function expenseDateFor(receipt: Receipt): string {
  return receipt.createdAt.slice(0, 10)
}

// ---------------------------------------------------------------------------
// The receipt OCR — what to say about it, if anything
// ---------------------------------------------------------------------------

/**
 * What the viewer says about the automatic read, or `null` for nothing to
 * say. This is the "it suggests, never decides" rule in words: `done` says
 * what was read and asks for it to be checked rather than presenting it as
 * fact, and `off` — a free account, or a PDF nobody could ask a vision model
 * about — says nothing at all, because there is nothing that happened.
 */
export function ocrNote(receipt: Receipt, currency: string): string | null {
  switch (receipt.ocrStatus) {
    case 'pending':
      return 'Reading the total…'
    case 'done':
      return receipt.ocrAmountCents === null
        ? null
        : `Looks like the total was ${formatMoney(receipt.ocrAmountCents, currency)} — worth checking before you file this.`
    case 'failed':
      return 'Could not read a total automatically. Enter it yourself when you file this.'
    case 'off':
      return null
  }
}

// ---------------------------------------------------------------------------
// The filter control
// ---------------------------------------------------------------------------

/** Which pile the grid is showing. The server's `?filter=`, as a value. */
export type LibraryFilter = 'all' | 'unfiled' | 'filed'

export const FILTERS: readonly { id: LibraryFilter; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'unfiled', label: 'Not filed yet' },
  { id: 'filed', label: 'On an expense' },
]

/**
 * What the grid says when it has nothing in it, which depends on why.
 *
 * "Nothing here" under a filter somebody chose is a screen that looks broken;
 * the sentence has to say which of the three questions was asked.
 */
export function emptyMessage(filter: LibraryFilter, uploader: string | null): string {
  if (uploader !== null) return 'Nobody has put anything here under that name yet.'
  switch (filter) {
    case 'unfiled':
      return 'Everything here is on an expense. Nothing is waiting to be filed.'
    case 'filed':
      return 'Nothing has been filed on an expense yet.'
    default:
      return 'No receipts in this group yet. Photograph one at the till and file it later.'
  }
}
